import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { ModalLoading } from 'components/ModalLoading';
import { RequestStatus } from 'features/request/requestSlice';
import { Grid, GridItem, IconAlert, Informer } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ActKC2ConstructionTypeField } from '../AddActKC2/ActKC2ConstructionTypeField';
import { ActKC2CostGroupField } from '../AddActKC2/ActKC2CostGroupField';
import { ActKC2ObjectField } from '../AddActKC2/ActKC2ObjectField';
import { ActKC2SeasonField } from '../AddActKC2/ActKC2SeasonField';

import { useActKC2TemplateDownload } from './hooks/useActKC2TemplateDownload';

import Style from './ExportActKC2TemplateModal.module.css';

interface IExportActKC2TemplateModal {
    onClose: () => void;
    workOrderId: number;
}

const title = 'Выгрузить шаблон акта КС-2';

export const ExportActKC2TemplateModal: React.FC<IExportActKC2TemplateModal> = (props) => {
    const { onClose, workOrderId } = props;
    const { control, handleSubmit, reset, resetField } = useForm();

    const constructionType = useWatch({ control, name: 'constructionTypeId' });

    const onModalClose = useCallback(() => {
        reset();
        onClose();
    }, [onClose, reset]);

    const {
        isSubmittedDownload,
        exportActKC2TemplateAsExcelData,
        exportActKC2TemplateAsExcelStatus,
        exportActKC2TemplateAsExcelError,
        onActKC2AsExcelAbort,
        onDownloadActKc2Template,
        onCloseModalErrorDownload,
        onCreateReportDownload,
    } = useActKC2TemplateDownload(workOrderId, onModalClose);

    const onConfirm = useCallback(
        (submitData: Record<string, any>) => {
            onDownloadActKc2Template({
                constructionTypeId: submitData?.constructionTypeId?.id,
                costGroup: submitData?.costGroup?.id,
                workOrderObjectIds: submitData?.workOrderObjectIds?.map(({ id }: any) => id),
                workOrderSeasonId: submitData?.workOrderSeasonId?.id,
            });
        },
        [onDownloadActKc2Template],
    );

    useEffect(() => {
        resetField('workOrderObjectIds');
    }, [constructionType, resetField]);

    return (
        <>
            <ModalDialog
                title={title}
                btnConfirmLabel="Выгрузить"
                hasOverlay
                withTitleCloseButton
                onClose={onModalClose}
                onConfirm={handleSubmit(onConfirm)}
                btnConfirmDisabled={exportActKC2TemplateAsExcelStatus === RequestStatus.FETCHING}
                isOpen
            >
                <div className={Style.exportActKC2TemplateModal}>
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <Informer status="warning" size="s" icon={IconAlert}>
                                Заполните все поля для выгрузки шаблона Акта КС-2
                            </Informer>
                        </GridItem>
                        <GridItem>
                            <ActKC2SeasonField control={control} disabled={false} name="workOrderSeasonId" />
                        </GridItem>
                        <GridItem>
                            <ActKC2ConstructionTypeField
                                name="constructionTypeId"
                                rules={requiredBaseRule}
                                control={control}
                                useWorkOrderTypes
                                required
                            />
                        </GridItem>
                        <GridItem>
                            <ActKC2ObjectField
                                name="workOrderObjectIds"
                                control={control}
                                rules={requiredBaseRule}
                                constructionTypeId={constructionType?.id}
                                hasToShowObjectsOnly
                                isConstructionTypeDependent
                                required
                            />
                        </GridItem>
                        <GridItem>
                            <ActKC2CostGroupField
                                name="costGroup"
                                control={control}
                                rules={requiredBaseRule}
                                required
                            />
                        </GridItem>
                    </Grid>
                </div>
            </ModalDialog>
            <ErrorHandlingModal
                titleText={title}
                isSubmitted={isSubmittedDownload}
                statusRequest={exportActKC2TemplateAsExcelStatus}
                dataRequest={exportActKC2TemplateAsExcelData?.uerExportActKC2TemplateAsExcel}
                errorsRequest={exportActKC2TemplateAsExcelError}
                handleCloseModalError={onCloseModalErrorDownload}
                handleCreateReport={onCreateReportDownload}
            />
            {exportActKC2TemplateAsExcelStatus === RequestStatus.FETCHING && (
                <ModalLoading title={title} onClose={onActKC2AsExcelAbort} />
            )}
        </>
    );
};

ExportActKC2TemplateModal.displayName = 'ExportActKC2TemplateModal';
